import type { CSSProperties } from "react";
import { ColorType } from "@/theme/colors";
import { getRadiusCssValue } from "@/theme/radius";
import { pxToRem } from "@/theme/spacing";
import { MarkdownHeadingLanguageMode } from "./markdown-heading-model";
import type {
  MarkdownHeadingAccentBarConfig,
  MarkdownHeadingPresetConfig,
} from "./markdown-heading-types";

interface CreateMarkdownHeadingWrapperStyleInput {
  config: MarkdownHeadingPresetConfig;
  languageMode: MarkdownHeadingLanguageMode;
  style?: CSSProperties;
}

interface CreateMarkdownHeadingAccentBarStyleInput {
  accentBar: MarkdownHeadingAccentBarConfig;
  size: number;
}

interface CreateMarkdownHeadingTextStyleInput {
  config: MarkdownHeadingPresetConfig;
}

const RTL_BLOCK_STYLE: CSSProperties = { direction: "rtl" };
const DEFAULT_BLOCK_STYLE: CSSProperties = {};

const BLOCK_STYLE_BY_LANGUAGE_MODE: Record<
  MarkdownHeadingLanguageMode,
  CSSProperties
> = {
  [MarkdownHeadingLanguageMode.Default]: DEFAULT_BLOCK_STYLE,
  [MarkdownHeadingLanguageMode.Hebrew]: RTL_BLOCK_STYLE,
};

const ACCENT_BAR_HEX = "rgb(102, 254, 193)";
const ACCENT_BAR_GLOW = "0 0 8px rgba(102, 254, 193, 0.45)";
const DEFAULT_ACCENT_BAR_COLOR = "rgba(255, 255, 255, 0.4)";

const ACCENT_BAR_COLOR_BY_TYPE: Partial<Record<ColorType, string>> = {
  [ColorType.Accent]: ACCENT_BAR_HEX,
};

const ACCENT_BAR_GLOW_BY_TYPE: Partial<Record<ColorType, string>> = {
  [ColorType.Accent]: ACCENT_BAR_GLOW,
};

export function createMarkdownHeadingWrapperStyle({
  config,
  languageMode,
  style,
}: CreateMarkdownHeadingWrapperStyleInput): CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: config.accentBar ? pxToRem(10) : 0,
    marginTop: pxToRem(config.marginTopPx),
    marginBottom: pxToRem(config.marginBottomPx),
    ...BLOCK_STYLE_BY_LANGUAGE_MODE[languageMode],
    ...style,
  };
}

export function createMarkdownHeadingAccentBarStyle({
  accentBar,
  size,
}: CreateMarkdownHeadingAccentBarStyleInput): CSSProperties {
  return {
    flexShrink: 0,
    width: pxToRem(accentBar.widthPx),
    alignSelf: "stretch",
    minHeight: pxToRem(Math.round(size * 0.9)),
    borderRadius: getRadiusCssValue(accentBar.widthPx),
    backgroundColor: resolveAccentBarColor({ color: accentBar.color }),
    boxShadow: ACCENT_BAR_GLOW_BY_TYPE[accentBar.color],
  };
}

export function createMarkdownHeadingTextStyle({
  config,
}: CreateMarkdownHeadingTextStyleInput): CSSProperties {
  return {
    maxWidth: "100%",
    letterSpacing: config.letterSpacing,
    textTransform: config.textTransform,
  };
}

function resolveAccentBarColor({ color }: { color: ColorType }): string {
  return ACCENT_BAR_COLOR_BY_TYPE[color] ?? DEFAULT_ACCENT_BAR_COLOR;
}
